import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  CircleDashed,
  Info,
  X,
} from '@plataforma/ui/iconos';
import { createContext, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useApi } from './api';
import { EVENTO_CAMBIO } from './cambios';
import { ContextoConfig } from './config';
import { useSesion } from './sesion';

/** Contrato de `@plataforma/servicio-base` (preparacion.ts), tal como llega por el Gateway. */
export type EstadoPunto = 'listo' | 'falta' | 'revisar' | 'no_aplica';
export interface PuntoPreparacion {
  clave: string;
  grupo: string;
  titulo: string;
  estado: EstadoPunto;
  obligatorio: boolean;
  detalle: string | null;
  pantalla: string | null;
  ayuda?: string | null;
}
interface RespuestaPreparacion {
  servicio: string;
  puntos: PuntoPreparacion[];
}

export interface EstadoPreparacion {
  puntos: PuntoPreparacion[];
  porcentaje: number;
  /** Faltan o hay que revisar puntos obligatorios. */
  incompleta: boolean;
  /** Servicios que no respondieron: sus puntos no se conocen y el % no se puede dar por bueno. */
  sinRespuesta: string[];
  cargando: boolean;
}

/**
 * % configurado (arquitectura 2.25): obligatorios listos entre los obligatorios que aplican;
 * «revisar» no cuenta como listo y los recomendados no bajan el %. Sin obligatorios, 100.
 */
export function porcentajeConfigurado(puntos: readonly PuntoPreparacion[]): number {
  const aplican = puntos.filter((p) => p.obligatorio && p.estado !== 'no_aplica');
  if (!aplican.length) return 100;
  return Math.floor((aplican.filter((p) => p.estado === 'listo').length * 100) / aplican.length);
}

/** El servicio de la ruta del Gateway: `/sourcing/superadmin/preparacion` → `sourcing`. */
const servicioDe = (ruta: string) => ruta.split('/')[1] ?? ruta;

/**
 * Junta la preparación de los servicios que le tocan al panel. Cada servicio evalúa solo sus
 * datos; si uno no responde, sus puntos faltan y se dice cuál, sin tirar la barra completa.
 */
export function usePreparacion(rutas: readonly string[] | null): EstadoPreparacion {
  const api = useApi();
  const [estado, setEstado] = useState<Omit<EstadoPreparacion, 'porcentaje' | 'incompleta'>>({
    puntos: [],
    sinRespuesta: [],
    cargando: true,
  });
  const clave = rutas?.join('|') ?? '';
  // Se vuelve a leer al guardar algo en el panel y al regresar a la pestaña: sin esto el % no
  // avanzaba hasta recargar la página.
  const [vez, setVez] = useState(0);
  useEffect(() => {
    if (!rutas) return;
    let espera: ReturnType<typeof setTimeout> | undefined;
    const otraVez = () => {
      clearTimeout(espera);
      espera = setTimeout(() => setVez((v) => v + 1), 400);
    };
    const alVolver = () => document.visibilityState === 'visible' && otraVez();
    window.addEventListener(EVENTO_CAMBIO, otraVez);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      clearTimeout(espera);
      window.removeEventListener(EVENTO_CAMBIO, otraVez);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [clave]);
  useEffect(() => {
    if (!rutas) return;
    let vivo = true;
    void Promise.allSettled(rutas.map((r) => api<RespuestaPreparacion>('GET', r))).then(
      (respuestas) => {
        if (!vivo) return;
        setEstado({
          puntos: respuestas.flatMap((r) =>
            r.status === 'fulfilled' ? (r.value?.puntos ?? []) : [],
          ),
          sinRespuesta: rutas
            .filter((_, i) => respuestas[i]?.status === 'rejected')
            .map(servicioDe),
          cargando: false,
        });
      },
    );
    return () => {
      vivo = false;
    };
  }, [api, clave, vez]);
  return {
    ...estado,
    porcentaje: porcentajeConfigurado(estado.puntos),
    incompleta: estado.puntos.some(
      (p) => p.obligatorio && (p.estado === 'falta' || p.estado === 'revisar'),
    ),
  };
}

interface SaludServicio {
  servicio: string;
  listo: boolean;
}

/** Salud de los servicios que publica el Gateway (`GET /api/estado`), cada minuto. */
export function useSalud(): SaludServicio[] | null {
  const api = useApi();
  const [salud, setSalud] = useState<SaludServicio[] | null>(null);
  useEffect(() => {
    let vivo = true;
    const leer = () =>
      api<{ servicios: SaludServicio[] }>('GET', '/estado')
        .then((r) => vivo && setSalud(r.servicios))
        .catch(() => vivo && setSalud(null));
    void leer();
    const cada = setInterval(() => void leer(), 60_000);
    return () => {
      vivo = false;
      clearInterval(cada);
    };
  }, [api]);
  return salud;
}

/** Cómo lo dice el lotero: por lo que usa, no por el nombre del servicio (2.25). */
const PARA_EL_LOTERO: Record<string, string> = {
  platform: 'Cuenta y suscripción',
  commerce: 'Tienda y pagos',
  sourcing: 'Subastas e importación',
  inventory: 'Vehículos y fotos',
  finance: 'Costos y tipo de cambio',
  workshop: 'Taller y proveedores',
  channels: 'Canales de venta',
};

function resumenSalud(salud: SaludServicio[] | null, simple: boolean) {
  if (!salud) return { bien: null, texto: 'Sin datos de los servicios' };
  const caidos = salud.filter((s) => !s.listo).map((s) => s.servicio);
  if (!caidos.length) return { bien: true, texto: 'Todo funciona' };
  const nombres = simple ? caidos.map((s) => PARA_EL_LOTERO[s] ?? s) : caidos;
  return { bien: false, texto: `${nombres.join(', ')} con falla` };
}

const AMBIENTES: Record<string, string> = {
  dev: 'DEV',
  qa: 'QA',
  'prod-1': 'PROD-1',
  prod1: 'PROD-1',
  prod: 'PROD',
  local: 'LOCAL',
};
const nombreAmbiente = (a: string) => AMBIENTES[a.toLowerCase()] ?? (a.toUpperCase() || 'LOCAL');

export interface ConsumoBarra {
  /** «IA 32 % del mes», «Plan Lotero · IA 12 %». */
  texto: string;
  porcentaje: number | null;
  a: string;
}

/**
 * Barra de estado (arquitectura 2.25): una tira delgada bajo la barra superior con el %
 * configurado, la salud de los servicios, el ambiente y la versión y el consumo. En el teléfono
 * va compacta y al tocarla abre el detalle en una hoja que queda arriba de la barra inferior y
 * debajo del aviso de cookies.
 */
export function BarraEstado({
  preparacion,
  equipo,
  verPorcentaje = true,
  consumo,
}: {
  preparacion: EstadoPreparacion;
  /** Paneles del equipo: ambiente con color y servicios por nombre; el lotero, en simple. */
  equipo: boolean;
  /** En Admin Lotero, solo los roles que configuran ven el %. */
  verPorcentaje?: boolean;
  consumo?: ConsumoBarra | null | undefined;
}) {
  const config = useContext(ContextoConfig);
  const salud = useSalud();
  const [abierta, setAbierta] = useState(false);
  useEffect(() => {
    if (!abierta) return;
    const alTeclear = (e: KeyboardEvent) => e.key === 'Escape' && setAbierta(false);
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [abierta]);
  const ambiente = nombreAmbiente(config?.ambiente ?? '');
  const version = config?.version || 'local';
  const { bien, texto: textoSalud } = resumenSalud(salud, !equipo);
  const pct = preparacion.cargando ? null : preparacion.porcentaje;
  const piezas = (
    <>
      {verPorcentaje && (
        <Link
          to="/preparacion"
          className={`barra-estado-pieza${preparacion.incompleta ? ' pendiente' : ''}`}
          title="Ver lo que falta para dejarlo listo"
        >
          <span className="barra-estado-medidor" aria-hidden="true">
            <span style={{ width: `${pct ?? 0}%` }} />
          </span>
          {pct === null ? 'Configurado …' : `${pct} % configurado`}
        </Link>
      )}
      <span
        className={`barra-estado-pieza salud ${bien === null ? '' : bien ? 'bien' : 'mal'}`}
        role="status"
      >
        <span className="barra-estado-punto" aria-hidden="true" />
        {textoSalud}
      </span>
      {consumo && (
        <Link
          to={consumo.a}
          className={`barra-estado-pieza${(consumo.porcentaje ?? 0) >= 80 ? ' pendiente' : ''}`}
        >
          {consumo.texto}
        </Link>
      )}
      {equipo ? (
        <span className={`barra-estado-ambiente amb-${ambiente.toLowerCase()}`}>
          {ambiente} · {version}
        </span>
      ) : (
        <span className="barra-estado-version">v{version.replace(/^v/, '')}</span>
      )}
    </>
  );
  return (
    <>
      <div className="barra-estado" aria-label="Estado del servicio">
        <div className="barra-estado-escritorio">{piezas}</div>
        <button
          type="button"
          className="barra-estado-movil"
          aria-expanded={abierta}
          aria-controls="hoja-estado"
          onClick={() => setAbierta((a) => !a)}
        >
          {verPorcentaje && <strong>{pct === null ? '…' : `${pct} %`}</strong>}
          <span className={`barra-estado-punto ${bien === null ? '' : bien ? 'bien' : 'mal'}`} />
          <span className="barra-estado-texto">{textoSalud}</span>
          {equipo && (
            <span className={`barra-estado-ambiente amb-${ambiente.toLowerCase()}`}>
              {ambiente}
            </span>
          )}
          <ChevronRight size={16} aria-hidden="true" />
        </button>
      </div>
      {abierta && (
        <div className="hoja-estado-fondo" onClick={() => setAbierta(false)} aria-hidden="true" />
      )}
      <div
        id="hoja-estado"
        className="hoja-estado"
        role="dialog"
        aria-label="Estado del servicio"
        hidden={!abierta}
        onClickCapture={(e) => {
          if ((e.target as HTMLElement).closest('a')) setAbierta(false);
        }}
      >
        <header>
          <strong>Estado del servicio</strong>
          <button type="button" className="ui-hoja-cerrar" onClick={() => setAbierta(false)}>
            <X size={20} aria-hidden="true" />
            <span className="ui-oculto">Cerrar</span>
          </button>
        </header>
        <div className="hoja-estado-piezas">{piezas}</div>
        {equipo && salud && (
          <ul className="hoja-estado-servicios">
            {salud.map((s) => (
              <li key={s.servicio}>
                <span className={`barra-estado-punto ${s.listo ? 'bien' : 'mal'}`} />
                {s.servicio}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

const hoy = () => new Date().toLocaleDateString('en-CA');
function leerCerrado(llave: string) {
  try {
    return localStorage.getItem(llave);
  } catch {
    return null;
  }
}
function guardarCerrado(llave: string) {
  try {
    localStorage.setItem(llave, hoy());
  } catch {
    // Sin almacenamiento (ventana privada): el aviso vuelve a salir al recargar; no pasa nada.
  }
}

const porGrupo = (puntos: PuntoPreparacion[]) =>
  puntos.reduce<Record<string, PuntoPreparacion[]>>((g, p) => {
    (g[p.grupo] ??= []).push(p);
    return g;
  }, {});

function IconoEstado({ estado }: { estado: EstadoPunto }) {
  if (estado === 'listo')
    return <CheckCircle2 size={18} className="estado-listo" aria-label="Listo" />;
  if (estado === 'revisar')
    return <AlertTriangle size={18} className="estado-revisar" aria-label="Revisar" />;
  if (estado === 'no_aplica')
    return <Info size={18} className="estado-no-aplica" aria-label="No aplica" />;
  return <CircleDashed size={18} className="estado-falta" aria-label="Falta" />;
}

function Punto({ p }: { p: PuntoPreparacion }) {
  return (
    <li className={`punto-preparacion ${p.estado}`}>
      <IconoEstado estado={p.estado} />
      <div>
        <strong>{p.titulo}</strong>
        {p.detalle && <small>{p.detalle}</small>}
        {p.ayuda && (
          <small>
            <Link to={p.ayuda}>Cómo se hace</Link>
          </small>
        )}
      </div>
      {p.pantalla && p.estado !== 'listo' && p.estado !== 'no_aplica' && (
        <Link className="boton" to={p.pantalla}>
          {p.estado === 'revisar' ? 'Revisar' : 'Configurar'}
          <ArrowRight size={16} aria-hidden="true" />
        </Link>
      )}
    </li>
  );
}

function Medidor({ porcentaje }: { porcentaje: number }) {
  return (
    <div
      className="medidor-preparacion"
      role="progressbar"
      aria-valuenow={porcentaje}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label="Configurado"
    >
      <span style={{ width: `${porcentaje}%` }} />
    </div>
  );
}

/**
 * «Para dejarlo listo te falta…» al entrar (arquitectura 2.25): solo si falta algo obligatorio.
 * Avisa, no bloquea: va arriba de la página, se cierra y vuelve a salir al día siguiente mientras
 * falte algo.
 */
export function AvisoPreparacion({ preparacion }: { preparacion: EstadoPreparacion }) {
  const { usuario } = useSesion();
  const llave = `preparacion-aviso:${usuario?.id ?? 'anonimo'}`;
  const [cerrado, setCerrado] = useState(() => leerCerrado(llave) === hoy());
  if (cerrado || preparacion.cargando || !preparacion.incompleta) return null;
  const pendientes = preparacion.puntos.filter(
    (p) => p.obligatorio && (p.estado === 'falta' || p.estado === 'revisar'),
  );
  const cerrar = () => {
    guardarCerrado(llave);
    setCerrado(true);
  };
  return (
    <section className="seccion aviso-preparacion" aria-labelledby="aviso-preparacion">
      <header>
        <h2 id="aviso-preparacion">Para dejarlo listo te falta…</h2>
        <button type="button" className="boton" onClick={cerrar}>
          Ahora no
        </button>
      </header>
      <div className="cuerpo">
        <p>
          Llevas <strong>{preparacion.porcentaje} %</strong> configurado. Puedes seguir trabajando;
          esto vuelve a salir mañana mientras falte algo.
        </p>
        <Medidor porcentaje={preparacion.porcentaje} />
        {Object.entries(porGrupo(pendientes)).map(([grupo, puntos]) => (
          <div key={grupo} className="grupo-preparacion">
            <h3>{grupo}</h3>
            <ul>
              {puntos.map((p) => (
                <Punto key={p.clave} p={p} />
              ))}
            </ul>
          </div>
        ))}
        <Link to="/preparacion">Ver la lista completa</Link>
      </div>
    </section>
  );
}

/** La preparación que ya juntó el marco del panel, para la página `/preparacion`. */
export const ContextoPreparacion = createContext<EstadoPreparacion | null>(null);

/** Lista completa de la preparación del panel (`/preparacion`), a la que lleva la barra. */
export function PaginaPreparacion() {
  const preparacion = useContext(ContextoPreparacion) ?? {
    puntos: [],
    porcentaje: 100,
    incompleta: false,
    sinRespuesta: [],
    cargando: true,
  };
  const obligatorios = preparacion.puntos.filter((p) => p.obligatorio && p.estado !== 'no_aplica');
  const recomendados = preparacion.puntos.filter((p) => !p.obligatorio && p.estado !== 'no_aplica');
  const noAplican = preparacion.puntos.filter((p) => p.estado === 'no_aplica');
  return (
    <>
      <h1>Configuración</h1>
      <p className="subtitulo">
        Lo que se configura en este panel y cómo está cada punto. Los recomendados no bajan el %.
      </p>
      {preparacion.cargando ? (
        <p className="cargando">Cargando…</p>
      ) : (
        <>
          <section className="seccion">
            <header>
              <h2>{preparacion.porcentaje} % configurado</h2>
            </header>
            <div className="cuerpo">
              <Medidor porcentaje={preparacion.porcentaje} />
              {preparacion.sinRespuesta.length > 0 && (
                <p className="aviso">
                  No respondió: {preparacion.sinRespuesta.join(', ')}. Sus puntos no aparecen.
                </p>
              )}
            </div>
          </section>
          {[
            { titulo: 'Obligatorio', puntos: obligatorios },
            { titulo: 'Recomendado', puntos: recomendados },
            { titulo: 'No aplica', puntos: noAplican },
          ]
            .filter((s) => s.puntos.length)
            .map((s) => (
              <section key={s.titulo} className="seccion" aria-label={s.titulo}>
                <header>
                  <h2>{s.titulo}</h2>
                </header>
                <div className="cuerpo">
                  {Object.entries(porGrupo(s.puntos)).map(([grupo, puntos]) => (
                    <div key={grupo} className="grupo-preparacion">
                      <h3>{grupo}</h3>
                      <ul>
                        {puntos.map((p) => (
                          <Punto key={p.clave} p={p} />
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>
            ))}
        </>
      )}
    </>
  );
}
